import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import './ShowroomInvitation.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/* ── i18n helper ── */
const translations = {};
const loadTranslations = async (lang) => {
  if (translations[lang]) return translations[lang];
  try {
    const mod = await import(`../i18n/${lang}.json`);
    translations[lang] = mod.default?.invitation || {};
    return translations[lang];
  } catch { return {}; }
};

const displayI18n = (val, lang) => {
  if (!val) return '';
  if (typeof val === 'string') return val;
  return val[lang] || val.en || Object.values(val)[0] || '';
};

/* ── Passcode Input Component ── */
const PasscodeInput = ({ onSubmit, error, loading, t }) => {
  const [digits, setDigits] = useState(['', '', '', '']);
  const refs = [useRef(null), useRef(null), useRef(null), useRef(null)];

  useEffect(() => { refs[0].current?.focus(); }, []);

  const handleChange = (idx, val) => {
    if (!/^\d?$/.test(val)) return;
    const next = [...digits];
    next[idx] = val;
    setDigits(next);
    if (val && idx < 3) refs[idx + 1].current?.focus();
    if (val && idx === 3) {
      const code = [...next].join('');
      if (code.length === 4 && !loading) setTimeout(() => onSubmit(code), 150);
    }
  };

  const handleKeyDown = (idx, e) => {
    if (e.key === 'Backspace' && !digits[idx] && idx > 0) refs[idx - 1].current?.focus();
    if (e.key === 'Enter') {
      const code = digits.join('');
      if (code.length === 4 && !loading) onSubmit(code);
    }
  };

  const handlePaste = (e) => {
    const text = (e.clipboardData.getData('text') || '').replace(/\D/g, '').slice(0, 4);
    if (text.length === 4) {
      setDigits(text.split(''));
      refs[3].current?.focus();
      e.preventDefault();
      setTimeout(() => onSubmit(text), 150);
    }
  };

  const code = digits.join('');
  const canSubmit = code.length === 4 && !loading;

  return (
    <div className="inv-passcode-area" data-testid="inv-passcode-area">
      <div className="inv-passcode-card">
        <div className="inv-passcode-icon">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0110 0v4" />
          </svg>
        </div>
        <p className="inv-passcode-label">{t('enterPasscode')}</p>
        <div className="inv-passcode-digits" onPaste={handlePaste}>
          {digits.map((d, i) => (
            <input key={i} ref={refs[i]} type="text" inputMode="numeric" pattern="[0-9]*"
              maxLength={1} value={d} onChange={e => handleChange(i, e.target.value)}
              onKeyDown={e => handleKeyDown(i, e)} autoComplete="off"
              data-testid={`inv-passcode-digit-${i}`}
              className={`inv-digit-input ${error ? 'inv-digit-error' : ''}`} />
          ))}
        </div>
        {error && <p className="inv-passcode-error" data-testid="inv-passcode-error">{error}</p>}
        <button onClick={() => canSubmit && onSubmit(code)} disabled={!canSubmit}
          data-testid="inv-passcode-submit"
          className={`inv-enter-btn ${canSubmit ? 'inv-enter-btn-active' : 'inv-enter-btn-disabled'}`}>
          {loading ? t('verifying') : t('enterExperience')}
          {!loading && <span className="inv-arrow">→</span>}
        </button>
      </div>
    </div>
  );
};

/* ── Experience Card ── */
const ExperienceCard = ({ exp, index, onClick, t }) => {
  const verticalIcons = {
    restaurant: '🍽️', hotel: '🏨', automotive: '🚗', property: '🏢',
    airport: '✈️', casino: '🎰', healthcare: '🏥', stadium: '🏟️',
    warehouse: '📦', retail: '🛍️', restroom: '🚻', delivery: '🚚',
  };
  const icon = Object.entries(verticalIcons).find(([k]) =>
    (exp.vertical || '').toLowerCase().includes(k) || (exp.name || '').toLowerCase().includes(k)
  )?.[1] || '◇';

  return (
    <button onClick={onClick} data-testid={`inv-exp-card-${exp.id}`}
      className="inv-exp-card" style={{ animationDelay: `${0.1 + index * 0.1}s` }}>
      {exp.background_url && (
        <div className="inv-exp-card-bg">
          <img src={exp.background_url.startsWith('http') ? exp.background_url : `${API_URL}${exp.background_url}`}
            alt="" loading="lazy" />
        </div>
      )}
      <div className="inv-exp-card-content">
        <span className="inv-exp-icon">{icon}</span>
        <h3 className="inv-exp-name">{exp.name}</h3>
        <div className="inv-exp-cta">
          {t('exploreExperience')} <span className="inv-arrow-sm">→</span>
        </div>
      </div>
    </button>
  );
};

/* ── Experience Viewer (renders full TapCard experience) ── */
const ExperienceViewer = ({ data, lang, onBack, t }) => {
  const { experience, allowed_modes, mode_labels } = data;
  const [sceneIdx, setSceneIdx] = useState(0);
  const [activeCard, setActiveCard] = useState(null);
  const [cardModes, setCardModes] = useState({});
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);

  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);

  if (!experience || !experience.scenes?.length) return null;
  const scenes = experience.scenes;
  const scene = scenes[sceneIdx];
  const cards = scene?.cards || [];

  const resolveImg = (url) => {
    if (!url) return '/yulontap_card_logo.png?v=2';
    return url.startsWith('http') ? url : `${API_URL}${url}`;
  };

  const handleCardTap = (card) => {
    setActiveCard(card.id);
    const modeKey = cardModes[card.id];
    const dests = card.destinations || {};
    const url = modeKey ? dests[modeKey] : (Object.values(dests)[0] || null);
    if (url) {
      setTimeout(() => {
        window.open(url, '_blank', 'noopener,noreferrer');
        setActiveCard(null);
      }, 400);
    }
  };

  const bgUrl = scene.background_url
    ? (scene.background_url.startsWith('http') ? scene.background_url : `${API_URL}${scene.background_url}`)
    : null;

  return (
    <div className="inv-experience-viewer" data-testid="inv-experience-viewer">
      <button onClick={onBack} className="inv-back-btn" data-testid="inv-back-btn">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M19 12H5M12 19l-7-7 7-7" />
        </svg>
        {t('backToExperiences')}
      </button>

      <div className="inv-exp-stage" style={bgUrl ? { backgroundImage: `url(${bgUrl})`, backgroundSize: 'cover', backgroundPosition: 'center' } : {}}>
        <div className="inv-exp-stage-overlay" />
        <div className={`inv-cards-container ${isMobile ? 'inv-cards-mobile' : 'inv-cards-desktop'}`}>
          {cards.map((card, idx) => {
            const isActive = activeCard === card.id;
            const hasCustomArt = !!card.card_image_url;
            const dests = Object.entries(card.destinations || {});
            const selectedMode = cardModes[card.id];

            return (
              <div key={card.id} className="inv-tap-card-wrap"
                style={!isMobile ? { position: 'absolute', left: `${card.position_x}%`, top: `${card.position_y}%`, transform: 'translate(-50%,-50%)' } : {}}
                data-testid={`inv-tap-card-${card.id}`}>
                <button onClick={() => handleCardTap(card)}
                  className={`inv-tap-card ${isActive ? 'inv-tap-active' : ''}`}
                  style={{ animationDelay: `${0.3 + idx * 0.15}s` }}>
                  <div className={`inv-tap-card-inner ${hasCustomArt ? 'inv-tap-custom' : 'inv-tap-default'}`}>
                    <img src={resolveImg(card.card_image_url)} alt={displayI18n(card.label, lang) || 'Card'}
                      className="inv-tap-card-img" draggable={false} />
                    {!hasCustomArt && (
                      <div className="inv-tap-card-label">
                        <span>{displayI18n(card.label, lang)}</span>
                      </div>
                    )}
                  </div>
                </button>
                {dests.length > 1 && (
                  <div className="inv-mode-pills">
                    {dests.map(([key]) => (
                      <button key={key}
                        onClick={() => setCardModes(p => ({ ...p, [card.id]: key }))}
                        className={`inv-mode-pill ${selectedMode === key ? 'inv-mode-active' : ''}`}>
                        {mode_labels[key] || key}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {scenes.length > 1 && (
        <div className="inv-scene-nav">
          {scenes.map((_, i) => (
            <button key={i} onClick={() => setSceneIdx(i)}
              className={`inv-scene-dot ${i === sceneIdx ? 'inv-scene-dot-active' : ''}`}
              data-testid={`inv-scene-dot-${i}`} />
          ))}
        </div>
      )}
    </div>
  );
};

/* ── Feedback Section ── */
const FeedbackSection = ({ token, sessionId, recipientName, t }) => {
  const [text, setText] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!text.trim() || submitting) return;
    setSubmitting(true);
    try {
      await fetch(`${API_URL}/api/invitations/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, session_id: sessionId, text: text.trim() }),
      });
      setSubmitted(true);
    } catch { /* silent */ }
    setSubmitting(false);
  };

  const firstName = (recipientName || '').split(' ')[0];

  return (
    <div className="inv-thankyou" data-testid="inv-thankyou">
      <div className="inv-thankyou-inner">
        <h2 className="inv-thankyou-title">{t('thankYou')}{firstName ? `, ${firstName}` : ''}</h2>
        <p className="inv-thankyou-text">{t('thankYouMessage')}</p>
        {!submitted ? (
          <>
            <p className="inv-thankyou-prompt">{t('feedbackPrompt')}</p>
            <textarea value={text} onChange={e => setText(e.target.value)}
              placeholder={t('commentPlaceholder')}
              className="inv-comment-box" data-testid="inv-comment-box"
              rows={3} maxLength={1000} />
            <button onClick={handleSubmit} disabled={!text.trim() || submitting}
              className={`inv-submit-btn ${text.trim() ? 'inv-submit-active' : ''}`}
              data-testid="inv-submit-feedback">
              {submitting ? '...' : t('submit')}
            </button>
          </>
        ) : (
          <p className="inv-submitted-msg" data-testid="inv-feedback-submitted">{t('submitted')}</p>
        )}
        <div className="inv-contact-cta">
          <a href="/en/contact" target="_blank" rel="noopener noreferrer"
            className="inv-contact-btn" data-testid="inv-contact-btn">
            {t('contactUs')}
          </a>
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════
   MAIN PAGE
   ═══════════════════════════════════════════════════ */
const ShowroomInvitationPage = () => {
  const { token: rawToken } = useParams();
  const token = (rawToken || '').split('\n')[0].split('%0A')[0].trim();
  const [lang, setLang] = useState('en');
  const [strings, setStrings] = useState({});
  const [phase, setPhase] = useState('loading'); // loading | welcome | hub | experience | error
  const [invitation, setInvitation] = useState(null);
  const [experiences, setExperiences] = useState([]);
  const [sessionId, setSessionId] = useState('');
  const [currentExp, setCurrentExp] = useState(null);
  const [error, setError] = useState('');
  const [passcodeError, setPasscodeError] = useState('');
  const [passcodeLoading, setPasscodeLoading] = useState(false);

  const t = useCallback((key) => strings[key] || key, [strings]);

  useEffect(() => { loadTranslations(lang).then(setStrings); }, [lang]);

  // Initial load — fetch invitation personalization
  useEffect(() => {
    const init = async () => {
      try {
        const res = await fetch(`${API_URL}/api/invitations/validate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token }),
        });
        if (!res.ok) {
          const d = await res.json().catch(() => ({}));
          setError(d.detail || 'Invalid invitation');
          setPhase('error');
          return;
        }
        const data = await res.json();
        if (data.requires_passcode) {
          setInvitation({ recipient_name: data.recipient_name, company: data.company });
          setPhase('welcome');
        } else if (data.authenticated) {
          setInvitation({ recipient_name: data.recipient_name, company: data.company, message: data.message });
          setExperiences(data.experiences || []);
          setSessionId(data.session_id);
          setPhase('hub');
        }
      } catch {
        setError('Connection error');
        setPhase('error');
      }
    };
    if (token) init();
  }, [token]);

  const handlePasscodeSubmit = async (code) => {
    setPasscodeError('');
    setPasscodeLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/invitations/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, passcode: code }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        if (d.detail?.includes('expired') || d.detail?.includes('Expired')) {
          setError(d.detail);
          setPhase('error');
          return;
        }
        setPasscodeError(d.detail === 'Invalid passcode' ? t('incorrectPasscode') : (d.detail || 'Access denied'));
        return;
      }
      const data = await res.json();
      setInvitation({ recipient_name: data.recipient_name, company: data.company, message: data.message });
      setExperiences(data.experiences || []);
      setSessionId(data.session_id);
      setPhase('hub');
    } catch {
      setPasscodeError('Connection error');
    } finally {
      setPasscodeLoading(false);
    }
  };

  const openExperience = async (expId) => {
    try {
      const res = await fetch(`${API_URL}/api/invitations/experience`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, session_id: sessionId, experience_id: expId, lang }),
      });
      if (!res.ok) return;
      const data = await res.json();
      setCurrentExp(data);
      setPhase('experience');
      window.scrollTo(0, 0);
    } catch { /* silent */ }
  };

  const backToHub = () => {
    setCurrentExp(null);
    setPhase('hub');
    window.scrollTo(0, 0);
  };

  const firstName = (invitation?.recipient_name || '').split(' ')[0];

  // ── RENDER ──
  if (phase === 'loading') {
    return (
      <div className="inv-page inv-loading" data-testid="inv-loading">
        <div className="inv-loader" />
      </div>
    );
  }

  if (phase === 'error') {
    const isExpired = error.toLowerCase().includes('expired');
    const isRevoked = error.toLowerCase().includes('revoked');
    return (
      <div className="inv-page" data-testid="inv-error">
        <div className="inv-header"><img src="/tapelligence_logo_transparent.png" alt="Tapelligence" className="inv-logo" /></div>
        <div className="inv-error-content">
          <div className="inv-error-icon">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              {isExpired ? <><circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" /></> :
               isRevoked ? <><circle cx="12" cy="12" r="10" /><path d="M15 9l-6 6M9 9l6 6" /></> :
               <><circle cx="12" cy="12" r="10" /><path d="M12 8v4M12 16h.01" /></>}
            </svg>
          </div>
          <h1 className="inv-error-title">{isExpired ? t('expired') : isRevoked ? t('revoked') : 'Error'}</h1>
          <p className="inv-error-msg">{isExpired ? t('expiredMessage') : isRevoked ? t('revokedMessage') : error}</p>
        </div>
      </div>
    );
  }

  if (phase === 'welcome') {
    const introText = invitation?.company
      ? t('introPersonalized').replace('{company}', invitation.company)
      : t('introDefault');

    return (
      <div className="inv-page" data-testid="inv-welcome">
        <div className="inv-hero-bg" />
        <div className="inv-header">
          <img src="/tapelligence_logo_transparent.png" alt="Tapelligence" className="inv-logo" loading="eager" />
          <div className="inv-lang-selector" data-testid="inv-lang-selector">
            {['en', 'fr', 'es'].map((l, i) => (
              <span key={l}>
                {i > 0 && <span className="inv-lang-sep">|</span>}
                <button onClick={() => setLang(l)} className={`inv-lang-btn ${lang === l ? 'inv-lang-active' : ''}`}>{l.toUpperCase()}</button>
              </span>
            ))}
          </div>
        </div>

        <div className="inv-welcome-content">
          <div className="inv-accent-words">
            <span>{t('simple')}</span><span>{t('smart')}</span><span>{t('powerful')}</span>
          </div>

          <h1 className="inv-welcome-heading" data-testid="inv-welcome-heading">
            <span className="inv-welcome-label">{t('welcome')}</span> <span className="inv-name-highlight">{firstName || ''}</span>
          </h1>
          <p className="inv-welcome-subtitle">{t('yourShowroom')}</p>

          <p className="inv-welcome-intro">{introText}</p>
          <p className="inv-welcome-hint">{t('enterPasscodeHint')}</p>

          <PasscodeInput onSubmit={handlePasscodeSubmit} error={passcodeError} loading={passcodeLoading} t={t} />

          <div className="inv-quote">
            <span className="inv-quote-text">"{t('quoteText')}"</span>
          </div>
        </div>

        <div className="inv-value-bar">
          <div className="inv-value-item">
            <svg className="inv-value-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4-4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/></svg>
            <span className="inv-value-label">{t('engage')}</span>
            <span className="inv-value-sub">{t('engageSub')}</span>
          </div>
          <div className="inv-value-item">
            <svg className="inv-value-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M7 17V13M12 17V7M17 17V11"/></svg>
            <span className="inv-value-label">{t('gain')}</span>
            <span className="inv-value-sub">{t('gainSub')}</span>
          </div>
          <div className="inv-value-item">
            <svg className="inv-value-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/></svg>
            <span className="inv-value-label">{t('streamline')}</span>
            <span className="inv-value-sub">{t('streamlineSub')}</span>
          </div>
          <div className="inv-value-item">
            <svg className="inv-value-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
            <span className="inv-value-label">{t('grow')}</span>
            <span className="inv-value-sub">{t('growSub')}</span>
          </div>
        </div>

        <div className="inv-trust-bar">
          <p className="inv-trust-label">{t('trustedBy')}</p>
          <div className="inv-trust-icons">
            <span>{t('restaurants')}</span>
            <span className="inv-trust-sep">|</span>
            <span>{t('hotels')}</span>
            <span className="inv-trust-sep">|</span>
            <span>{t('properties')}</span>
            <span className="inv-trust-sep">|</span>
            <span>{t('automotive')}</span>
            <span className="inv-trust-sep">|</span>
            <span>{t('andMore')}</span>
          </div>
        </div>
      </div>
    );
  }

  if (phase === 'experience' && currentExp) {
    return (
      <div className="inv-page" data-testid="inv-experience-phase">
        <div className="inv-header inv-header-compact">
          <img src="/tapelligence_logo_transparent.png" alt="Tapelligence" className="inv-logo-sm" />
        </div>
        <ExperienceViewer data={currentExp} lang={lang} onBack={backToHub} t={t} />
      </div>
    );
  }

  // phase === 'hub'
  return (
    <div className="inv-page" data-testid="inv-hub">
      <div className="inv-header">
        <img src="/tapelligence_logo_transparent.png" alt="Tapelligence" className="inv-logo" />
        <div className="inv-lang-selector">
          {['en', 'fr', 'es'].map((l, i) => (
            <span key={l}>
              {i > 0 && <span className="inv-lang-sep">|</span>}
              <button onClick={() => setLang(l)} className={`inv-lang-btn ${lang === l ? 'inv-lang-active' : ''}`}>{l.toUpperCase()}</button>
            </span>
          ))}
        </div>
      </div>

      <div className="inv-hub-content">
        <div className="inv-hub-welcome">
          <h1 className="inv-hub-heading">
            <span className="inv-welcome-label">{t('welcome')}</span> <span className="inv-name-highlight">{firstName}</span>
          </h1>
          {invitation?.message && <p className="inv-hub-message">{invitation.message}</p>}
          <p className="inv-hub-subtitle">{t('selectedExperiences')}</p>
        </div>

        <div className="inv-exp-grid" data-testid="inv-exp-grid">
          {experiences.map((exp, i) => (
            <ExperienceCard key={exp.id} exp={exp} index={i} onClick={() => openExperience(exp.id)} t={t} />
          ))}
        </div>
      </div>

      <FeedbackSection token={token} sessionId={sessionId} recipientName={invitation?.recipient_name} t={t} />

      <div className="inv-footer">
        <img src="/tapelligence_logo_transparent.png" alt="Tapelligence" className="inv-footer-logo" />
        <p className="inv-footer-tagline">{t('tagline')}</p>
      </div>
    </div>
  );
};

export default ShowroomInvitationPage;
